import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Plus, Loader2, RefreshCw, Search, ArrowDownAZ, Eye, EyeOff,
  AlertTriangle, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABELS = {
  ACQUIRING: "Acquiring", ONRAMP: "Onramp", CRYPTO: "Crypto",
  PAYOUT: "Payout", OTHER: "Other",
};
const METHOD_LABELS = {
  CARDS: "Cards", APM: "APM", CRYPTO: "Crypto", BANK: "Bank", OTHER: "Other",
};
const REGION_LABELS = {
  EU: "EU", UK: "UK", US: "US", LATAM: "LATAM", APAC: "APAC",
  ROW: "ROW", WW: "Global",
};

const GROUP_BY_OPTIONS = [
  { value: "none", label: "No grouping" },
  { value: "product_type", label: "Product type" },
  { value: "provider", label: "Provider" },
  { value: "region", label: "Region" },
  { value: "status", label: "Status" },
];

function StatusPill({ status }) {
  const tone = status === "active"
    ? "bg-emerald-500/[0.10] text-emerald-300 border-emerald-400/30"
    : "bg-zinc-500/[0.10] text-zinc-400 border-zinc-500/30";
  return (
    <span
      data-testid={`solution-status-pill-${status}`}
      className={`inline-flex items-center text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full border ${tone}`}
    >
      {status}
    </span>
  );
}

function MarginCell({ margin, alerts, revealed }) {
  if (!revealed) {
    return (
      <span className="text-zinc-600 font-mono select-none text-[12px]" data-testid="margin-masked">
        ••••
      </span>
    );
  }
  if (!margin) {
    return <span className="text-zinc-600 text-[11.5px]">—</span>;
  }
  const dr = margin.discount_rate_pct ?? 0;
  const hasAlert = alerts && alerts.length > 0;
  const tone = hasAlert ? "text-amber-300" : "text-emerald-300";
  return (
    <span
      data-testid="margin-cell"
      className={`inline-flex items-center gap-1 font-mono tabular-nums text-[12px] ${tone}`}
      title={hasAlert ? alerts.join(", ") : "Healthy margin"}
    >
      {hasAlert && <AlertTriangle className="h-3 w-3" />}
      {dr.toFixed(2)}pp
    </span>
  );
}

const CREATE_RATE_FIELDS = [
  { key: "discount_rate_pct", label: "Discount %" },
  { key: "transaction_fee_amount", label: "Per-txn" },
  { key: "monthly_fee_amount", label: "Monthly" },
  { key: "setup_fee_amount", label: "Setup" },
  { key: "chargeback_fee_amount", label: "Chargeback" },
  { key: "rolling_reserve_pct", label: "Reserve %" },
  { key: "rolling_reserve_days", label: "Reserve days" },
];

function RateGrid({ values, setValues, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
      {CREATE_RATE_FIELDS.map((f) => (
        <div key={f.key}>
          <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">{f.label}</label>
          <input
            type="number"
            step={f.key === "rolling_reserve_days" ? "1" : "0.01"}
            min="0"
            value={values[f.key]}
            onChange={(e) => setValues({ ...values, [f.key]: Number(e.target.value) })}
            data-testid={`${testIdPrefix}-${f.key}`}
            className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-2 py-1.5 text-[12px] text-white focus:outline-none focus:border-[#00E5FF]/40"
          />
        </div>
      ))}
    </div>
  );
}

function CreateSolutionModal({ open, onClose, currencies, onCreated }) {
  const empty = {
    discount_rate_pct: 0, transaction_fee_amount: 0, monthly_fee_amount: 0,
    setup_fee_amount: 0, chargeback_fee_amount: 0, rolling_reserve_pct: 0,
    rolling_reserve_days: 0,
  };
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [internalCode, setInternalCode] = useState("");
  const [settlementCurrency, setSettlementCurrency] = useState("EUR");
  const [midCurrency, setMidCurrency] = useState("EUR");
  const [buyRates, setBuyRates] = useState(empty);
  const [sellRates, setSellRates] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName(""); setProvider(""); setInternalCode("");
    setSettlementCurrency("EUR"); setMidCurrency("EUR");
    setBuyRates(empty); setSellRates(empty);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/solutions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          provider: provider.trim(),
          internal_code: internalCode.trim() || null,
          settlement_currency: settlementCurrency,
          mid_currency: midCurrency,
          buy_rates: buyRates,
          sell_rates: sellRates,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = Array.isArray(data?.detail)
          ? data.detail.map((d) => d.msg || d).join("; ")
          : data?.detail || `${r.status}`;
        throw new Error(msg);
      }
      toast.success(`Created ${data.name}`);
      onCreated(data);
      reset();
      onClose();
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8">
      <form
        onSubmit={submit}
        data-testid="solution-create-modal"
        className="w-full max-w-3xl mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-[#00E5FF]" />
          <h2 className="text-[14px] font-medium text-white">New solution</h2>
        </div>
        <p className="text-[12px] text-zinc-500 mb-5">
          Product type, method and region are auto-decoded from the public name + internal code (e.g. ACQ-CC-EU-USDT). Capability flags and 3DS posture are parsed from notes after you save.
        </p>

        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">Public name</label>
            <input
              required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="solution-create-name"
              placeholder="ACQ-CC-EU-USDT"
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/40"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">Settlement</label>
            <select
              value={settlementCurrency} onChange={(e) => setSettlementCurrency(e.target.value)}
              data-testid="solution-create-settlement-currency"
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#00E5FF]/40"
            >
              {(currencies || []).map((c) => (
                <option key={c.code} value={c.code}>{c.code}</option>
              ))}
              {(!currencies || currencies.length === 0) && (
                <option value={settlementCurrency}>{settlementCurrency}</option>
              )}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">MID ccy</label>
            <select
              value={midCurrency} onChange={(e) => setMidCurrency(e.target.value)}
              data-testid="solution-create-mid-currency"
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#00E5FF]/40"
            >
              {(currencies || []).map((c) => (
                <option key={c.code} value={c.code}>{c.code}</option>
              ))}
              {(!currencies || currencies.length === 0) && (
                <option value={midCurrency}>{midCurrency}</option>
              )}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-amber-300 mb-1.5">Provider (visible)</label>
            <input
              required value={provider} onChange={(e) => setProvider(e.target.value)}
              data-testid="solution-create-provider"
              placeholder="Worldpay / Stripe / ..."
              className="w-full rounded-xl border border-amber-400/30 bg-amber-400/[0.04] px-3 py-2 text-[13px] text-white focus:outline-none focus:border-amber-400/60"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">Internal code</label>
            <input
              value={internalCode} onChange={(e) => setInternalCode(e.target.value.toUpperCase())}
              data-testid="solution-create-internal-code"
              placeholder="VIC-NP-ACQ-CC"
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white font-mono focus:outline-none focus:border-[#00E5FF]/40"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-5">
          <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.03] p-4">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-300 mb-3">— Buy rates (cost)</div>
            <RateGrid values={buyRates} setValues={setBuyRates} testIdPrefix="solution-create-buy" />
          </div>
          <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.03] p-4">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-300 mb-3">— Sell rates (merchant)</div>
            <RateGrid values={sellRates} setValues={setSellRates} testIdPrefix="solution-create-sell" />
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button
            type="submit" disabled={submitting}
            data-testid="solution-create-submit"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />} Create
          </button>
        </div>
      </form>
    </div>
  );
}

function RevealModal({ open, onClose, onRevealed }) {
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/solutions/reveal-all-buy-rates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, reason: reason.trim() || null }),
      });
      const data = await r.json();
      if (!r.ok) {
        if (r.status === 423) throw new Error(data?.detail || "Locked");
        if (r.status === 412) throw new Error("No reveal password set — ask super_admin via /admin/team.");
        throw new Error(data?.detail || `${r.status}`);
      }
      toast.success("Buy rates revealed for 15 minutes");
      setPassword(""); setReason("");
      onRevealed();
      onClose();
    } catch (err) {
      toast.error(String(err.message || "Reveal failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <form onSubmit={submit} data-testid="reveal-all-modal"
        className="w-full max-w-md mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="h-4 w-4 text-amber-400" />
          <h2 className="text-[14px] font-medium text-white">Reveal buy rates (all)</h2>
        </div>
        <p className="text-[12px] text-zinc-500 mb-5">
          Unlocks buy rates + margin numbers across every solution on this session for 15 minutes.
        </p>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">Reveal password</label>
        <input
          type="password" required value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="reveal-all-password-input"
          autoFocus
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-4 focus:outline-none focus:border-amber-400/40"
        />
        <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">Reason (audited)</label>
        <input
          type="text" value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="reveal-all-reason-input"
          placeholder="e.g. preparing quotes for new pipeline"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-5 focus:outline-none focus:border-amber-400/40"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="reveal-all-submit-btn"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50 inline-flex items-center gap-1.5">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />} Reveal
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminSolutions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [productFilter, setProductFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [settleFilter, setSettleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("none");
  const [currencies, setCurrencies] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);

  const anyRevealed = useMemo(
    () => rows.some((r) => r.buy_rates_revealed),
    [rows],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/solutions?status=${statusFilter}`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setRows(data.solutions || []);
    } catch (err) {
      toast.error("Failed to load solutions");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/admin/currencies`, { credentials: "include" });
        if (r.ok) {
          const data = await r.json();
          setCurrencies(data.currencies || []);
        }
      } catch (_) { /* non-fatal */ }
    })();
  }, []);

  // Derive available filter options from the loaded rows.
  const settleOptions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.settlement_currency && s.add(r.settlement_currency));
    return Array.from(s).sort();
  }, [rows]);

  // Filter + search.
  const filtered = useMemo(() => {
    let out = rows;
    if (productFilter !== "all") out = out.filter((r) => r.product_type === productFilter);
    if (regionFilter !== "all") out = out.filter((r) => r.region === regionFilter);
    if (settleFilter !== "all") out = out.filter((r) => r.settlement_currency === settleFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.provider || "").toLowerCase().includes(q) ||
        (r.internal_code || "").toLowerCase().includes(q),
      );
    }
    return out;
  }, [rows, productFilter, regionFilter, settleFilter, search]);

  // Group rows for rendering.
  const groups = useMemo(() => {
    if (groupBy === "none") return [{ label: null, rows: filtered }];
    const map = new Map();
    filtered.forEach((r) => {
      const k = r[groupBy] || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([label, rs]) => ({ label, rows: rs }));
  }, [filtered, groupBy]);

  const renderRow = (s) => {
    const sell = s.sell_rates || {};
    const settle = s.settlement_currency || "—";
    return (
      <tr
        key={s.solution_id}
        data-testid={`solution-row-${s.solution_id}`}
        className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
      >
        <td className="py-2.5 px-4">
          <Link
            to={`/admin/solutions/${s.solution_id}`}
            className="text-[12.5px] text-white hover:text-[#00E5FF] inline-flex items-center gap-1.5"
            data-testid={`solution-name-link-${s.solution_id}`}
          >
            {s.name}
            <ChevronRight className="h-3 w-3 opacity-50" />
          </Link>
          <div className="text-[10.5px] text-zinc-600 mt-0.5 font-mono" data-testid={`solution-row-provider-${s.solution_id}`}>
            {s.provider}
          </div>
        </td>
        <td className="py-2.5 px-3 text-[11.5px] text-zinc-400" data-testid={`solution-row-region-${s.solution_id}`}>
          <span className="inline-block px-1.5 py-0.5 rounded border border-white/[0.06] bg-white/[0.02] text-zinc-300">
            {REGION_LABELS[s.region] || s.region || "—"}
          </span>
          {s.countries && s.countries.length > 0 && (
            <div className="text-[10px] text-zinc-600 mt-0.5 font-mono truncate max-w-[120px]">
              {s.countries.slice(0, 3).join(",")}{s.countries.length > 3 ? "…" : ""}
            </div>
          )}
        </td>
        <td className="py-2.5 px-3 text-[11.5px]" data-testid={`solution-row-product-${s.solution_id}`}>
          <div className="text-zinc-300">{PRODUCT_LABELS[s.product_type] || s.product_type}</div>
          <div className="text-[10px] text-zinc-600 mt-0.5">{METHOD_LABELS[s.method] || s.method}</div>
        </td>
        <td className="py-2.5 px-3 text-[12px] text-white font-mono tabular-nums text-right" data-testid={`solution-row-sell-mdr-${s.solution_id}`}>
          {sell.discount_rate_pct != null ? `${Number(sell.discount_rate_pct).toFixed(2)}%` : "—"}
        </td>
        <td className="py-2.5 px-3 text-[12px] text-white font-mono tabular-nums text-right" data-testid={`solution-row-sell-pertxn-${s.solution_id}`}>
          {sell.transaction_fee_amount != null ? Number(sell.transaction_fee_amount).toFixed(2) : "—"}
        </td>
        <td className="py-2.5 px-3 text-[11.5px] text-zinc-300 font-mono" data-testid={`solution-row-settle-${s.solution_id}`}>
          {settle}
        </td>
        <td className="py-2.5 px-3 text-right" data-testid={`solution-row-margin-${s.solution_id}`}>
          <MarginCell margin={s.margin} alerts={s.margin_alerts} revealed={s.buy_rates_revealed} />
        </td>
        <td className="py-2.5 px-3"><StatusPill status={s.status} /></td>
      </tr>
    );
  };

  return (
    <div data-testid="admin-solutions-page">
      <header className="flex items-start justify-between mb-6 gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— Catalog</div>
          <h1 className="text-[22px] font-light text-white">Solutions</h1>
          <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl">
            Master catalog of resalable payment routes. Provider names visible by default — buy rates and margin gated behind the reveal password.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {anyRevealed ? (
            <span
              data-testid="reveal-active-pill"
              className="inline-flex items-center gap-1.5 text-[10.5px] text-emerald-300 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] px-2.5 py-1"
            >
              <Eye className="h-3 w-3" /> Buy rates revealed
            </span>
          ) : (
            <button
              onClick={() => setRevealOpen(true)}
              data-testid="reveal-all-btn"
              className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[12px] text-amber-300 hover:bg-amber-400/[0.12] inline-flex items-center gap-1.5"
            >
              <EyeOff className="h-3.5 w-3.5" /> Reveal buy rates
            </button>
          )}
          <button
            onClick={load}
            data-testid="solutions-refresh-btn"
            className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-2 text-zinc-400 hover:text-white"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="solutions-new-btn"
            className="rounded-xl px-3 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 inline-flex items-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> New solution
          </button>
        </div>
      </header>

      {/* Filter strip */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="solutions-filter-strip">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="solutions-search-input"
            placeholder="Search name / provider / code…"
            className="w-72 rounded-lg border border-white/[0.08] bg-white/[0.02] pl-8 pr-3 py-1.5 text-[12px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/40"
          />
        </div>
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
          data-testid="solutions-product-filter"
          className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-zinc-300 focus:outline-none">
          <option value="all">All products</option>
          {Object.entries(PRODUCT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}
          data-testid="solutions-region-filter"
          className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-zinc-300 focus:outline-none">
          <option value="all">All regions</option>
          {Object.entries(REGION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={settleFilter} onChange={(e) => setSettleFilter(e.target.value)}
          data-testid="solutions-settle-filter"
          className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-zinc-300 focus:outline-none">
          <option value="all">All settlements</option>
          {settleOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="solutions-status-filter"
          className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-zinc-300 focus:outline-none">
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="all">All status</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          <ArrowDownAZ className="h-3.5 w-3.5 text-zinc-500" />
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
            data-testid="solutions-groupby-select"
            className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-zinc-300 focus:outline-none">
            {GROUP_BY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-zinc-500" data-testid="solutions-loading">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-zinc-500" data-testid="solutions-empty">
            <Sparkles className="h-6 w-6 mx-auto mb-3 text-zinc-700" />
            <div className="text-[13px] mb-1">No solutions match the filters</div>
            <div className="text-[11.5px] text-zinc-600">Adjust filters or clear the search.</div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                <th className="text-left py-2.5 px-4 font-normal">Title / provider</th>
                <th className="text-left py-2.5 px-3 font-normal">Region</th>
                <th className="text-left py-2.5 px-3 font-normal">Product</th>
                <th className="text-right py-2.5 px-3 font-normal">Sell MDR</th>
                <th className="text-right py-2.5 px-3 font-normal">Sell per-txn</th>
                <th className="text-left py-2.5 px-3 font-normal">Settle</th>
                <th className="text-right py-2.5 px-3 font-normal">Margin</th>
                <th className="text-left py-2.5 px-3 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, gi) => (
                <Fragment key={g.label || `group-${gi}`}>
                  {g.label && (
                    <tr className="bg-white/[0.02]" data-testid={`solutions-group-${g.label}`}>
                      <td colSpan={8} className="py-2 px-4 text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                        {g.label} <span className="text-zinc-600 ml-2">({g.rows.length})</span>
                      </td>
                    </tr>
                  )}
                  {g.rows.map(renderRow)}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CreateSolutionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        currencies={currencies}
        onCreated={() => load()}
      />
      <RevealModal
        open={revealOpen}
        onClose={() => setRevealOpen(false)}
        onRevealed={() => load()}
      />
    </div>
  );
}
